/**
 * lexicalRuns — convert between serialized Lexical state (the Y.Doc storage
 * format) and flat formatting runs (the bridge format the native rich-text view
 * speaks). The native UITextView/EditText is the editing surface; these pure
 * functions are the only glue: load = state→runs, save = runs→state.
 *
 * Inline formatting (bold/italic/underline) round-trips via the TextNode format
 * bitmask. Block STRUCTURE is flattened — blocks join with "\n" on the way out
 * and split back into paragraphs on the way in; heading/list/quote types do not
 * survive (that's the documented block-structure deferral — a flat attributed
 * string can't represent them without paragraph-style mapping).
 */
import {
  $getRoot,
  $isTextNode,
  $isElementNode,
  $createParagraphNode,
  $createTextNode,
  type LexicalNode,
  type ElementNode,
} from "lexical";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  type HeadingTagType,
} from "@lexical/rich-text";
import {
  $createListNode,
  $createListItemNode,
  $isListNode,
  $isListItemNode,
  type ListNode,
} from "@lexical/list";

import { createNativeEditor, serializeEditor } from "./lexicalEdit.js";

export interface RichRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

// Lexical TextNode.format bitmask (BOLD=1, ITALIC=2, STRIKETHROUGH=4,
// UNDERLINE=8, CODE=16) — we surface the three the native view supports.
const IS_BOLD = 1;
const IS_ITALIC = 2;
const IS_UNDERLINE = 8;

function collect(node: LexicalNode, out: RichRun[]): void {
  if ($isTextNode(node)) {
    const f = node.getFormat();
    out.push({
      text: node.getTextContent(),
      bold: (f & IS_BOLD) !== 0,
      italic: (f & IS_ITALIC) !== 0,
      underline: (f & IS_UNDERLINE) !== 0,
    });
  } else if ($isElementNode(node)) {
    for (const child of node.getChildren()) {
      // Don't descend into a nested list — that's a deeper block, handled by
      // collectList; the mixed item's own text stops here.
      if ($isListNode(child)) continue;
      collect(child, out);
    }
  }
}

function sameFormat(a: RichRun, b: RichRun): boolean {
  return (
    !!a.bold === !!b.bold &&
    !!a.italic === !!b.italic &&
    !!a.underline === !!b.underline
  );
}

/** Merge adjacent runs with identical formatting (incl. across the "\n" seam). */
function merge(runs: RichRun[]): RichRun[] {
  const out: RichRun[] = [];
  for (const run of runs) {
    const last = out[out.length - 1];
    if (last && sameFormat(last, run)) last.text += run.text;
    else out.push({ ...run });
  }
  return out;
}

/** Serialized Lexical state → flat runs (blocks joined by "\n"). */
export function lexicalStateToRuns(state: string | undefined): RichRun[] {
  const editor = createNativeEditor(state);
  const runs: RichRun[] = [];
  editor.getEditorState().read(() => {
    const blocks = $getRoot().getChildren();
    blocks.forEach((block, bi) => {
      if (bi > 0) runs.push({ text: "\n" });
      collect(block, runs);
    });
  });
  return merge(runs);
}

function formatBits(run: RichRun): number {
  let f = 0;
  if (run.bold) f |= IS_BOLD;
  if (run.italic) f |= IS_ITALIC;
  if (run.underline) f |= IS_UNDERLINE;
  return f;
}

/** Flat runs → serialized Lexical state (one paragraph per "\n"-split line). */
export function runsToLexicalState(runs: RichRun[]): string {
  const editor = createNativeEditor();
  editor.update(
    () => {
      const root = $getRoot();
      root.clear();
      let para = $createParagraphNode();
      root.append(para);
      for (const run of runs) {
        const fmt = formatBits(run);
        const lines = run.text.split("\n");
        lines.forEach((line, i) => {
          if (i > 0) {
            para = $createParagraphNode();
            root.append(para);
          }
          if (line.length > 0) {
            const node = $createTextNode(line);
            if (fmt) node.setFormat(fmt);
            para.append(node);
          }
        });
      }
    },
    { discrete: true },
  );
  return serializeEditor(editor);
}

// ── Block-aware variant ────────────────────────────────────────────────────
// The native view sends/receives BLOCKS (each a typed paragraph holding inline
// runs), so heading/quote/list block TYPES survive the round-trip — not just
// inline formatting. A Lexical ListNode expands to one "ul"/"ol" block per item;
// consecutive same-type item blocks regroup into a single ListNode on the way
// back. (List NESTING isn't modelled — nested items flatten to top level.)

export type BlockType =
  | "paragraph"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "quote"
  | "ul"
  | "ol";

/** Horizontal alignment of a block. Stored as the Lexical element format;
 *  "left" is the default and is never stored. */
export type BlockAlign = "left" | "center" | "right" | "justify";

export interface RichBlock {
  type: BlockType;
  runs: RichRun[];
  /** Nesting depth for list items (0 = top level). Ignored for non-list blocks. */
  level?: number;
  /** Omitted = left (the default). */
  align?: BlockAlign;
}

const STORED_ALIGNS: ReadonlySet<string> = new Set([
  "center",
  "right",
  "justify",
]);

/** A block's alignment as stored on its Lexical element, when not the default. */
function alignOf(node: ElementNode): BlockAlign | undefined {
  const f = node.getFormatType();
  return STORED_ALIGNS.has(f) ? (f as BlockAlign) : undefined;
}

function applyAlign(node: ElementNode, align: BlockAlign | undefined): void {
  if (align && align !== "left") node.setFormat(align);
}

function isListType(t: BlockType): boolean {
  return t === "ul" || t === "ol";
}

function blockTypeOf(node: LexicalNode): BlockType {
  if ($isHeadingNode(node)) return node.getTag() as BlockType;
  if ($isQuoteNode(node)) return "quote";
  return "paragraph";
}

function appendRuns(node: ElementNode, runs: RichRun[]): void {
  for (const run of runs) {
    if (run.text.length > 0) {
      const tn = $createTextNode(run.text);
      const fmt = formatBits(run);
      if (fmt) tn.setFormat(fmt);
      node.append(tn);
    }
  }
}

/**
 * Walk a (possibly nested) ListNode → flat blocks carrying their nesting depth.
 * Lexical nests via a ListItemNode whose child is another ListNode; such a
 * wrapper item emits no block of its own — we recurse one level deeper.
 */
function collectList(
  listNode: ListNode,
  depth: number,
  blocks: RichBlock[],
): void {
  const t: BlockType = listNode.getListType() === "number" ? "ol" : "ul";
  for (const item of listNode.getChildren()) {
    if (!$isListItemNode(item)) continue;
    // Lexical nests the sub-list INSIDE the item, so read the item's own text
    // (collect stops at the nested list), emit it, then recurse one level down.
    const nested = item.getChildren().find((c) => $isListNode(c));
    const runs = merge(
      (() => {
        const out: RichRun[] = [];
        collect(item, out);
        return out;
      })(),
    );
    if (runs.length > 0 || !nested) {
      const align = alignOf(item);
      blocks.push({ type: t, level: depth, runs, ...(align ? { align } : {}) });
    }
    if (nested && $isListNode(nested)) {
      collectList(nested, depth + 1, blocks);
    }
  }
}

/** Serialized Lexical state → typed blocks (lists expand to per-item blocks). */
export function lexicalStateToBlocks(state: string | undefined): RichBlock[] {
  const editor = createNativeEditor(state);
  const blocks: RichBlock[] = [];
  editor.getEditorState().read(() => {
    for (const node of $getRoot().getChildren()) {
      if ($isListNode(node)) {
        collectList(node, 0, blocks);
      } else {
        const runs: RichRun[] = [];
        collect(node, runs);
        const align = $isElementNode(node) ? alignOf(node) : undefined;
        blocks.push({
          type: blockTypeOf(node),
          runs: merge(runs),
          ...(align ? { align } : {}),
        });
      }
    }
  });
  return blocks;
}

function createBlockNode(type: BlockType) {
  if (type === "quote") return $createQuoteNode();
  if (type !== "paragraph") return $createHeadingNode(type as HeadingTagType);
  return $createParagraphNode();
}

/**
 * Reconstruct a run of consecutive list blocks (any nesting levels) into nested
 * ListNodes: a deeper level becomes a ListNode under the last item of the level
 * above. Returns the index just past the run.
 */
function appendListRun(
  blocks: RichBlock[],
  start: number,
  root: ElementNode,
): number {
  const stack: ListNode[] = [];
  let i = start;
  while (i < blocks.length && isListType(blocks[i]!.type)) {
    const b = blocks[i]!;
    const level = Math.max(0, b.level ?? 0);
    const listType = b.type === "ol" ? "number" : "bullet";
    if (stack.length === 0) {
      const ln = $createListNode(listType);
      root.append(ln);
      stack.push(ln);
    }
    while (stack.length - 1 > level) stack.pop();
    while (stack.length - 1 < level) {
      const parent = stack[stack.length - 1]!;
      const last = parent.getLastChild();
      const host = $isListItemNode(last) ? last : $createListItemNode();
      if (host !== last) parent.append(host);
      const nested = $createListNode(listType);
      host.append(nested);
      stack.push(nested);
    }
    const item = $createListItemNode();
    appendRuns(item, b.runs);
    applyAlign(item, b.align);
    stack[level]!.append(item);
    i++;
  }
  return i;
}

/** Typed blocks → serialized Lexical state (heading/quote/list/paragraph). */
export function blocksToLexicalState(blocks: RichBlock[]): string {
  const editor = createNativeEditor();
  editor.update(
    () => {
      const root = $getRoot();
      root.clear();
      const list = blocks.length
        ? blocks
        : [{ type: "paragraph" as BlockType, runs: [] }];
      let i = 0;
      while (i < list.length) {
        if (isListType(list[i]!.type)) {
          i = appendListRun(list, i, root);
        } else {
          const node = createBlockNode(list[i]!.type);
          appendRuns(node, list[i]!.runs);
          applyAlign(node, list[i]!.align);
          root.append(node);
          i++;
        }
      }
    },
    { discrete: true },
  );
  return serializeEditor(editor);
}
